// Puente entre la cotización y el gasto.
//
// Una línea del desglose ligada a un servicio de proveedor YA dice quién la
// respalda (`conceptoId` → `concepto.proveedorId`), pero esa verdad solo la
// leía el margen: el proveedor no quedaba asignado al evento y su costo no
// existía. Resultado: cobrabas la ambulancia y nunca registrabas los $8,000
// que te cuesta, así que el evento reportaba 100% de margen.
//
// Aquí la cotización manda también del lado del gasto, igual que ya manda
// sobre `precioAcordado` (ver utils/valorEvento.js): al guardarla, los
// proveedores que implica quedan asignados y sus costos registrados por pagar.
//
// REVERSIBILIDAD. Todo lo que este módulo crea va marcado —`origen:
// "cotizacion_auto"` en el costo, `origen: "cotizacion"` en la asignación—
// para poder retirarlo cuando la línea desaparece. Y se retira SOLO si nadie
// lo tocó, con el mismo criterio que `esCostoAutoIntacto`: quitar una línea
// no puede borrar un pago ni un monto negociado a mano.
//
// NUNCA DUPLICA. Si ya existe un costo para ese servicio en ese evento —lo
// haya creado la asignación manual (`proveedor_auto`) o lo haya capturado
// alguien a mano— la cotización no crea un segundo. Las dos puertas a la
// misma realidad ("este evento usa a este proveedor") escriben una sola vez.

import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { fechaDeDia } from "./dates";
import {
  proveedorDe,
  varianteDe,
  costoDeVariante,
  costoProveedorDeVariante,
  nombreConVariante,
  cantidadRealDeLinea,
} from "./catalogo";
import { costosDeEvento } from "./costos";
import { esSoloPlanner } from "./proveedores";

export const ORIGEN_COSTO = "cotizacion_auto";
export const ORIGEN_ASIGNACION = "cotizacion";

// Un servicio con variante es un costo distinto del mismo servicio sin ella
// (una ambulancia "básica" y una "con médico" no son la misma tarifa), así
// que la identidad del costo automático es el par servicio+variante.
export const claveLinea = (conceptoId, varianteId) =>
  `${conceptoId}::${varianteId || ""}`;

// A qué servicio apunta un costo. Un solo nombre para el enlace —
// `conceptoId`, el id del doc en venues/{id}/conceptos— lo escriba quien lo
// escriba: este módulo, la asignación de un proveedor, o una persona desde
// el evento. Hubo un segundo nombre (`recursoId`) para lo mismo y con él la
// mitad del código leía uno y la otra mitad el otro, así que un costo
// capturado a mano era invisible aquí y se creaba un duplicado.
const servicioDeCosto = (costo) => costo?.conceptoId || null;

const redondea2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Mismo criterio que `esCostoAutoIntacto` (utils/proveedores.js), para el
// origen de este módulo: sigue siendo automático, sigue por pagar, y su monto
// sigue siendo el que puso la tarifa.
const intacto = (costo) =>
  !!costo &&
  costo.origen === ORIGEN_COSTO &&
  (costo.estado || "por_pagar") === "por_pagar" &&
  Number(costo.monto) === Number(costo.montoOriginal);

// Un planner cobra COMISIÓN, no tarifa (FRA-355): valuarlo con la tarifa de
// un servicio produciría una segunda comisión con el monto equivocado. Es la
// misma guarda que `crearCostoAutoDeProveedor`, pero aquí el planner se
// reconoce desde el directorio, porque la línea solo conoce al servicio.
// Sin directorio a la mano (un admin abriendo el evento de otro venue) se
// asume que no lo es. Solo el planner PURO queda fuera: un planner que
// además da servicios (mobiliario, decoración) sí respalda sus líneas con
// costo, igual que cualquier proveedor — utils/proveedores.esSoloPlanner.
const esPlanner = (proveedor) => esSoloPlanner(proveedor);

/**
 * Qué proveedores y qué costos implica un desglose, agregando las líneas que
 * comparten servicio+variante (dos renglones de la misma planta de luz son un
 * solo costo de N horas, no dos costos).
 *
 * Devuelve una entrada por servicio contratado, con el monto ya valuado a la
 * tarifa del proveedor.
 */
export function respaldosDeCotizacion(desglose, conceptos, proveedores = []) {
  const conceptoPorId = {};
  (Array.isArray(conceptos) ? conceptos : []).forEach((c) => {
    if (c?.id) conceptoPorId[c.id] = c;
  });
  const proveedorPorId = {};
  (Array.isArray(proveedores) ? proveedores : []).forEach((p) => {
    if (p?.id) proveedorPorId[p.id] = p;
  });

  const porClave = new Map();
  (Array.isArray(desglose) ? desglose : []).forEach((linea) => {
    const concepto = linea?.conceptoId ? conceptoPorId[linea.conceptoId] : null;
    if (!concepto) return;
    // Un servicio propio del venue no es un gasto: el venue ya es dueño de la
    // cosa. Lo que se gaste en ella entra por su lado (recarga, reposición).
    const proveedorId = proveedorDe(concepto);
    if (!proveedorId) return;
    if (esPlanner(proveedorPorId[proveedorId])) return;

    const cantidad = cantidadRealDeLinea(linea);
    if (!(cantidad > 0)) return;

    const variante = varianteDe(concepto, linea.varianteId || null);
    const clave = claveLinea(concepto.id, variante?.id || null);
    const previo = porClave.get(clave);
    if (previo) {
      previo.cantidad += cantidad;
      previo.monto = costoDeVariante(concepto, variante, previo.cantidad);
      return;
    }
    porClave.set(clave, {
      clave,
      conceptoId: concepto.id,
      conceptoNombre: concepto.nombre || "",
      varianteId: variante?.id || null,
      concepto: nombreConVariante(concepto, variante),
      cantidad,
      costoUnitarioSnapshot: costoProveedorDeVariante(concepto, variante),
      monto: costoDeVariante(concepto, variante, cantidad),
      proveedorId,
      proveedorNombre:
        concepto.proveedorNombre || proveedorPorId[proveedorId]?.nombre || "",
    });
  });
  return [...porClave.values()];
}

/**
 * El plan de reconciliación entre lo que la cotización implica y lo que ya
 * existe en el evento. Puro: no escribe nada, se puede probar y se puede
 * enseñar antes de aplicarlo.
 */
export function planDeCotizacion({
  desglose,
  conceptos,
  costos,
  eventId,
  asignados = [],
  proveedores = [],
}) {
  const deseados = respaldosDeCotizacion(desglose, conceptos, proveedores);
  const propios = costosDeEvento(costos, eventId);

  // Índice por servicio+variante SIN mirar el origen: si ya hay un costo para
  // ese servicio, venga de donde venga, la cotización no crea otro.
  const costosPorClave = {};
  // Y la red para el costo que solo dice de QUIÉN es: capturado contra el
  // proveedor y no contra un servicio, no tiene `conceptoId` y la
  // coincidencia exacta no lo ve. Sin esta red, un evento donde ya habías
  // puesto la ambulancia en la cotización Y capturado sus $8,000 a mano
  // terminaba con $16,000 al tocar el desglose.
  //
  // El precio de la red: si un proveedor te da DOS servicios en la misma
  // cotización y ya hay un costo suyo sin ligar, no se crea ninguno de los
  // dos — no hay forma de saber cuál de los dos ya estaba pagado. Se prefiere
  // quedarse corto y que lo captures tú, antes que inventar dinero.
  const proveedoresConCostoSinLigar = new Set();
  propios.forEach((c) => {
    const servicioId = servicioDeCosto(c);
    if (!servicioId) {
      if (c?.proveedorId) proveedoresConCostoSinLigar.add(c.proveedorId);
      return;
    }
    const clave = claveLinea(servicioId, c.varianteId || null);
    (costosPorClave[clave] || (costosPorClave[clave] = [])).push(c);
  });

  const crear = [];
  const actualizar = [];
  const borrar = [];
  const clavesVigentes = new Set();

  deseados.forEach((d) => {
    clavesVigentes.add(d.clave);
    const existentes = costosPorClave[d.clave] || [];
    const mio = existentes.find((c) => c.origen === ORIGEN_COSTO);
    if (mio) {
      // Cambió la cantidad en la cotización → se revalúa, pero solo mientras
      // nadie haya negociado el monto ni lo haya pagado.
      //
      // Se revalúa a la tarifa CONGELADA en el costo, no a la vigente: mover
      // la cantidad de un renglón no puede repreciar en silencio lo que ya
      // estaba acordado. Si la ambulancia se valuó a $800/hora y hoy cuesta
      // $900, pasar de 10 a 12 horas da $9,600 —12 × $800—, no $10,800. La
      // tarifa nueva entra en los costos nuevos, y el salto vive en el
      // tarifaHistorial del servicio. Sin snapshot (docs viejos) se cae a la
      // vigente, que es lo único que hay.
      const congelada = Number(mio.costoUnitarioSnapshot);
      const unitaria = congelada > 0 ? congelada : Number(d.costoUnitarioSnapshot) || 0;
      const monto = redondea2(unitaria * d.cantidad);
      if (intacto(mio) && monto > 0 && Math.abs(Number(mio.monto) - monto) >= 0.01) {
        actualizar.push({
          ...d,
          costoUnitarioSnapshot: unitaria,
          monto,
          costoId: mio.id,
          montoAnterior: Number(mio.monto),
        });
      }
      return;
    }
    // Ya hay costo de otro origen para este servicio: es el mismo gasto
    // capturado por la otra puerta. No se duplica.
    if (existentes.length > 0) return;
    // Hay dinero de este proveedor en el evento que no se puede ligar a un
    // servicio (capturado a mano, antes de que existiera este puente).
    if (proveedoresConCostoSinLigar.has(d.proveedorId)) return;
    // El servicio no tiene tarifa de proveedor capturada: no hay nada que
    // valuar. El proveedor sí se asigna — que participe consta igual.
    if (!(d.monto > 0)) return;
    crear.push(d);
  });

  const borrarIds = new Set();
  propios.forEach((c) => {
    const servicioId = servicioDeCosto(c);
    if (c.origen !== ORIGEN_COSTO || !servicioId) return;
    if (clavesVigentes.has(claveLinea(servicioId, c.varianteId || null))) return;
    // La línea se fue. Si alguien tocó el costo, se conserva: la cotización
    // no tiene derecho a borrar trabajo de nadie.
    if (!intacto(c)) return;
    borrar.push(c);
    borrarIds.add(c.id);
  });

  // ── Asignaciones ──
  const asignadosActuales = Array.isArray(asignados) ? asignados : [];
  const yaAsignado = new Set(asignadosActuales.map((a) => a.proveedorId));
  const proveedoresVigentes = new Map();
  deseados.forEach((d) => {
    if (!proveedoresVigentes.has(d.proveedorId)) proveedoresVigentes.set(d.proveedorId, d);
  });

  const asignar = [...proveedoresVigentes.values()].filter(
    (d) => !yaAsignado.has(d.proveedorId)
  );

  // Se retira solo lo que puso la cotización, y solo si ya nada lo sostiene:
  // ni una línea del desglose, ni un costo que sobreviva a esta pasada.
  const conCostoVivo = new Set(
    propios.filter((c) => !borrarIds.has(c.id)).map((c) => c.proveedorId).filter(Boolean)
  );
  const desasignar = asignadosActuales.filter(
    (a) =>
      a.origen === ORIGEN_ASIGNACION &&
      !proveedoresVigentes.has(a.proveedorId) &&
      !conCostoVivo.has(a.proveedorId)
  );

  return {
    crear,
    actualizar,
    borrar,
    asignar,
    desasignar,
    hayCambios:
      crear.length > 0 ||
      actualizar.length > 0 ||
      borrar.length > 0 ||
      asignar.length > 0 ||
      desasignar.length > 0,
  };
}

/**
 * Lo que la cotización implica y TODAVÍA no está registrado, para pintarlo
 * sin escribir nada.
 *
 * Esta es la razón de que no exista migración. Un evento viejo no necesita
 * que nadie lo toque: su desglose ya dice quién respalda cada línea y cuánto
 * cuesta, así que el Balance lo deriva y lo enseña igual que si estuviera
 * capturado. Escribir sirve para OTRA cosa —marcar pagado, ajustar el monto,
 * que entre a Cuentas— y por eso es una acción aparte, no un requisito para
 * ver la verdad.
 *
 * No hay doble conteo por construcción: se apoya en el mismo plan que usa la
 * escritura, y ese plan ya descarta todo lo que tiene costo registrado (por
 * servicio, o por proveedor cuando el costo viejo no dice de qué servicio es).
 */
export function pendienteDeCotizacion({
  desglose,
  conceptos,
  costos,
  eventId,
  asignados = [],
  proveedores = [],
}) {
  const plan = planDeCotizacion({
    desglose,
    conceptos,
    costos,
    eventId,
    asignados,
    proveedores,
  });
  const montoEstimado = plan.crear.reduce((s, d) => s + d.monto, 0);
  // Costo estimado por proveedor, para que el margen por renglón deje de
  // decir 100% en algo que sí te cuesta.
  const estimadoPorProveedor = {};
  plan.crear.forEach((d) => {
    estimadoPorProveedor[d.proveedorId] =
      (estimadoPorProveedor[d.proveedorId] || 0) + d.monto;
  });
  return {
    // Los servicios implicados sin costo registrado.
    servicios: plan.crear,
    // Los proveedores que la cotización implica y que ni siquiera están
    // asignados al evento: la tarjeta de Proveedores los pinta derivados.
    proveedoresSinAsignar: plan.asignar,
    estimadoPorProveedor,
    montoEstimado,
    hayPendiente: plan.crear.length > 0 || plan.asignar.length > 0,
  };
}

/**
 * Recorta un plan a un solo proveedor, dejando fuera todo lo que retira.
 * Puro, para poder probarlo: es lo que hace que el botón de una fila mueva
 * esa fila y nada más.
 */
export function acotarPlan(plan, proveedorId) {
  if (!proveedorId) return plan;
  const crear = plan.crear.filter((d) => d.proveedorId === proveedorId);
  const actualizar = plan.actualizar.filter((d) => d.proveedorId === proveedorId);
  const asignar = plan.asignar.filter((d) => d.proveedorId === proveedorId);
  return {
    crear,
    actualizar,
    asignar,
    borrar: [],
    desasignar: [],
    hayCambios: crear.length > 0 || actualizar.length > 0 || asignar.length > 0,
  };
}

/** La lista de asignados que resulta de aplicar el plan. */
export function asignadosTrasPlan(asignados, plan, userId = null) {
  const fuera = new Set(plan.desasignar.map((a) => a.proveedorId));
  const base = (Array.isArray(asignados) ? asignados : []).filter(
    (a) => !fuera.has(a.proveedorId)
  );
  const nuevos = plan.asignar.map((d) => ({
    proveedorId: d.proveedorId,
    proveedorNombre: d.proveedorNombre,
    categoria: null,
    subcategoria: null,
    servicioId: d.conceptoId,
    servicioNombre: d.concepto,
    varianteId: d.varianteId,
    cantidad: d.cantidad,
    // La marca que hace reversible esta asignación.
    origen: ORIGEN_ASIGNACION,
    assignedAt: Timestamp.now(),
    assignedBy: userId || null,
  }));
  return [...base, ...nuevos];
}

/**
 * Aplica el plan. Devuelve un resumen para el aviso y la bitácora, o null si
 * no había nada que hacer.
 *
 * El ORDEN importa, igual que en `assignProveedorToEvent`: primero las
 * asignaciones, después los costos. Si un costo falla, el proveedor queda
 * bien asignado y el caller avisa; al revés dejaría un costo huérfano.
 */
export async function sincronizarCotizacion({
  venueId,
  eventId,
  desglose,
  conceptos,
  proveedores = [],
  userId = null,
  // Acota la aplicación a UN proveedor: es el "Asignar" de su propia fila.
  // Solo hace la parte aditiva (asignar + registrar su costo) — la limpieza
  // de lo que ya no respalda ninguna línea es del barrido completo, y no
  // tendría por qué dispararse porque alguien asignó a otro.
  soloProveedorId = null,
}) {
  if (!venueId || !eventId || !userId) return null;

  // El estado se lee FRESCO, no se recibe del caller. Las suscripciones de la
  // pantalla van un tick atrás de la escritura que acaba de ocurrir, y dos
  // ediciones seguidas del desglose leerían el mundo anterior — creando el
  // costo dos veces. Es el mismo motivo por el que assignProveedorToEvent
  // hace su getDoc en vez de confiar en el contexto.
  const eventRef = doc(
    db,
    "venues",
    venueId,
    "calendars",
    "default",
    "events",
    eventId
  );
  const [eventSnap, costosSnap] = await Promise.all([
    getDoc(eventRef),
    getDocs(
      query(collection(db, "venues", venueId, "costos"), where("eventId", "==", eventId))
    ),
  ]);
  if (!eventSnap.exists()) return null;
  const eventData = eventSnap.data();
  const costos = costosSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const asignados = Array.isArray(eventData.proveedoresAsignados)
    ? eventData.proveedoresAsignados
    : [];

  const plan = acotarPlan(
    planDeCotizacion({
      desglose,
      conceptos,
      costos,
      eventId,
      asignados,
      proveedores,
    }),
    soloProveedorId
  );
  if (!plan.hayCambios) return null;

  let proveedoresAsignados = asignados;
  if (plan.asignar.length > 0 || plan.desasignar.length > 0) {
    proveedoresAsignados = asignadosTrasPlan(asignados, plan, userId);
    await updateDoc(eventRef, { proveedoresAsignados });
  }

  const costosCol = collection(db, "venues", venueId, "costos");
  // El día del evento como "día calendario", no el instante (utils/dates.js).
  const fecha = fechaDeDia(eventData.startTime) || Timestamp.now();

  for (const d of plan.crear) {
    await addDoc(costosCol, {
      eventId,
      concepto: d.concepto,
      categoria: "Proveedores",
      proveedorId: d.proveedorId,
      proveedorNombre: d.proveedorNombre,
      conceptoId: d.conceptoId,
      conceptoNombre: d.conceptoNombre,
      varianteId: d.varianteId,
      cantidad: d.cantidad,
      // La tarifa con la que se valuó, congelada: cambiarla después no toca
      // este doc (para eso está el tarifaHistorial del servicio).
      costoUnitarioSnapshot: d.costoUnitarioSnapshot,
      monto: d.monto,
      montoOriginal: d.monto,
      fecha,
      estado: "por_pagar",
      fechaPago: null, // se captura el día que de verdad se pague (FRA-342)
      origen: ORIGEN_COSTO,
      metodo: null,
      notas: null,
      createdAt: serverTimestamp(),
      createdBy: userId,
      updatedAt: null,
      updatedBy: null,
    });
  }

  for (const u of plan.actualizar) {
    await updateDoc(doc(db, "venues", venueId, "costos", u.costoId), {
      concepto: u.concepto,
      cantidad: u.cantidad,
      costoUnitarioSnapshot: u.costoUnitarioSnapshot,
      monto: u.monto,
      // Sigue siendo "intacto" tras revaluarse: lo movió la tarifa, no una
      // persona. Si no se moviera, la siguiente pasada lo creería negociado.
      montoOriginal: u.monto,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  }

  for (const c of plan.borrar) {
    await deleteDoc(doc(db, "venues", venueId, "costos", c.id));
  }

  return {
    proveedoresAsignados,
    creados: plan.crear.length,
    actualizados: plan.actualizar.length,
    borrados: plan.borrar.length,
    asignados: plan.asignar.length,
    desasignados: plan.desasignar.length,
    montoCreado: plan.crear.reduce((s, d) => s + d.monto, 0),
    nombres: plan.asignar.map((d) => d.proveedorNombre).filter(Boolean),
  };
}
